import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { BarChart3, TrendingUp, Users, Play, Globe, Calendar, ChevronDown, ArrowUpRight, ArrowDownRight, Music2 } from 'lucide-react';

const Analytics = () => {
  const [timeframe, setTimeframe] = useState('7d');

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics</h1>
            <p className="text-gray-600">Track your music performance across platforms</p>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="bg-white border border-gray-200 rounded-xl px-4 py-2 text-gray-700 focus:outline-none focus:border-purple-500 transition-colors"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Streams"
            value="0"
            change="0%"
            isPositive={true}
            icon={<Play className="h-5 w-5" />}
          />
          <StatCard
            title="Monthly Listeners"
            value="0"
            change="0%"
            isPositive={true}
            icon={<Users className="h-5 w-5" />}
          />
          <StatCard
            title="Platform Reach"
            value="0"
            change="0"
            isPositive={true}
            icon={<Globe className="h-5 w-5" />}
          />
          <StatCard
            title="Avg. Daily Streams"
            value="0"
            change="0%"
            isPositive={true}
            icon={<TrendingUp className="h-5 w-5" />}
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <ChartCard
            title="Streams by Platform"
            chart={
              <div className="h-64 flex items-end justify-between gap-4 px-4">
                <Bar height={0} label="Spotify" value="0" />
                <Bar height={0} label="Apple" value="0" />
                <Bar height={0} label="Amazon" value="0" />
                <Bar height={0} label="YouTube" value="0" />
                <Bar height={0} label="Others" value="0" />
              </div>
            }
          />
          <ChartCard
            title="Geographic Distribution"
            chart={
              <div className="h-64 flex items-end justify-between gap-4 px-4">
                <Bar height={0} label="USA" value="0" />
                <Bar height={0} label="UK" value="0" />
                <Bar height={0} label="Germany" value="0" />
                <Bar height={0} label="Canada" value="0" />
                <Bar height={0} label="Others" value="0" />
              </div>
            }
          />
        </div>

        {/* Top Performing */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-xl shadow-purple-100/20">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Top Performing Tracks</h3>
            <div className="space-y-4">
              <TrackRow
                title="No tracks yet"
                streams="0"
                change="0%"
                isPositive={true}
              />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-xl shadow-purple-100/20">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Top Cities</h3>
            <div className="space-y-4">
              <CityRow city="No data yet" listeners="0" />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const StatCard = ({ title, value, change, isPositive, icon }: { title: string; value: string; change: string; isPositive: boolean; icon: React.ReactNode }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-xl shadow-purple-100/20">
    <div className="flex items-center justify-between mb-4">
      <div className="bg-purple-50 p-2 rounded-xl">
        {React.cloneElement(icon as React.ReactElement, {
          className: 'text-purple-600'
        })}
      </div>
      <div className={`flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
        <span className="text-sm font-medium">{change}</span>
      </div>
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
    <p className="text-gray-600 text-sm">{title}</p>
  </div>
);

const ChartCard = ({ title, chart }: { title: string; chart: React.ReactNode }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-xl shadow-purple-100/20">
    <h3 className="text-xl font-bold text-gray-900 mb-6">{title}</h3>
    {chart}
  </div>
);

const Bar = ({ height, label, value }: { height: number; label: string; value: string }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="relative w-12">
      <div
        className="w-full bg-purple-100 rounded-t-lg absolute bottom-0"
        style={{ height: `${height}%` }}
      >
        <div
          className="w-full bg-purple-500 rounded-t-lg absolute bottom-0 opacity-50"
          style={{ height: `${height}%` }}
        />
      </div>
    </div>
    <span className="text-sm text-gray-600">{label}</span>
    <span className="text-sm font-medium text-gray-900">{value}</span>
  </div>
);

const TrackRow = ({ title, streams, change, isPositive }: { title: string; streams: string; change: string; isPositive: boolean }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <div className="flex items-center gap-3">
      <div className="bg-purple-50 p-2 rounded-xl">
        <Music2 className="h-4 w-4 text-purple-600" />
      </div>
      <span className="font-medium text-gray-900">{title}</span>
    </div>
    <div className="flex items-center gap-4">
      <span className="text-gray-600">{streams}</span>
      <span className={isPositive ? 'text-green-600' : 'text-red-600'}>
        {change}
      </span>
    </div>
  </div>
);

const CityRow = ({ city, listeners }: { city: string; listeners: string }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <div className="flex items-center gap-3">
      <div className="bg-purple-50 p-2 rounded-xl">
        <Globe className="h-4 w-4 text-purple-600" />
      </div>
      <span className="font-medium text-gray-900">{city}</span>
    </div>
    <span className="text-gray-600">{listeners}</span>
  </div>
);

export default Analytics;